import { Route, Routes } from "react-router-dom";
import {
  Dashboard,
  Reports,
  HomeShop,
  CategoryProductReport,
  CurrencyTotals,
  PayablesReceivablesReport,
  BuyingReport,
  SellingReport,
} from "../../features/dashboard";
import {
  AddProducts,
  GetProducts,
  ProductSaleHistory,
  Stock,
  UpdateProducts,
} from "../../features/products";
import {
  AddCustomer,
  CombinedPayments,
  CustomerOrderDetail,
  CustomerOrderHistory,
  CustomerPaymentHistory,
  CustomerWorkspace,
  GetCustomer,
  IncreasePayment,
  Payment,
  PaymentRecipt,
  UpdateCustomer,
} from "../../features/customers";
import { CartShop, EditOrder, InfoOrder, TableOrder, CancellationsList } from "../../features/orders";
import { AddUnit, GetUnit, UpdateUnit } from "../../features/units";
import { AddCategory, GetCategory, UpdateCategory } from "../../features/categories";
import { GetUsers, GetRoles, NotFound, SystemSettings, ExchangeRates, Maintenance } from "../../features/shared";
import { PurchaseOrdersList, PurchaseOrderEditorPage, Vendors, VendorWorkspace } from "../../features/purchasing";
import PermissionGuard from "../../shared/ui/PermissionGuard";

const guarded = (permission, Component, props = {}) => (
  <PermissionGuard permission={permission}>
    <Component {...props} />
  </PermissionGuard>
);

const AppRoutes = ({
  cart,
  handleClick,
}) => {
  return (
    <Routes>
      {/* Dashboard */}
      <Route path="/" element={guarded("dashboard.view", Dashboard)} />

      {/* Products */}
      <Route path="/AddProducts" element={guarded("products.add", AddProducts)} />
      <Route path="/Stock" element={guarded("stock.view", Stock)} />
      <Route path="/vendors" element={guarded("vendors.index", Vendors)} />
      <Route path="/vendors/:id" element={guarded("vendors.index", VendorWorkspace)} />
      <Route
        path="/purchase-orders/new"
        element={
          <PermissionGuard permission="purchase_orders.index">
            <PermissionGuard permission="products.index">
              <PurchaseOrderEditorPage />
            </PermissionGuard>
          </PermissionGuard>
        }
      />
      <Route
        path="/purchase-orders/:id/edit"
        element={
          <PermissionGuard permission="purchase_orders.index">
            <PermissionGuard permission="products.index">
              <PurchaseOrderEditorPage />
            </PermissionGuard>
          </PermissionGuard>
        }
      />
      <Route
        path="/purchase-orders"
        element={
          <PermissionGuard permission="purchase_orders.index">
            <PermissionGuard permission="products.index">
              <PurchaseOrdersList />
            </PermissionGuard>
          </PermissionGuard>
        }
      />
      <Route path="/GetProducts" element={guarded("products.index", GetProducts)} />
      <Route path="/products/:id/history" element={guarded("products.index", ProductSaleHistory)} />
      <Route path="/GetProducts/UpdateProducts/:id" element={guarded("products.edit", UpdateProducts)} />

      {/* Customers */}
      <Route path="/AddCustomer" element={guarded("customers.add", AddCustomer)} />
      <Route path="/GetCustomer" element={guarded("customers.index", GetCustomer)} />
      <Route path="/GetCustomer/view/:id" element={guarded("customers.index", CustomerWorkspace)} />
      <Route path="/GetCustomer/UpdateCustomer/:id" element={guarded("customers.edit", UpdateCustomer)} />
      <Route path="/GetCustomer/Payment/:id" element={guarded("customers.payment", Payment)} />
      <Route path="/GetCustomer/IncreasePayment/:id" element={guarded("customers.payment", IncreasePayment)} />

      {/* Orders */}
      <Route path="/TableOrder" element={guarded("orders.index", TableOrder)} />
      <Route path="/TableOrder/InfoOrder/:id" element={guarded("orders.view", InfoOrder)} />
      <Route path="/cancellations" element={guarded("orders.cancellations", CancellationsList)} />
      <Route path="/HomeShop" element={<PermissionGuard permission="orders.add"><HomeShop handleClick={handleClick} /></PermissionGuard>} />
      <Route path="/CartShop" element={<PermissionGuard permission="orders.add"><CartShop cart={cart} /></PermissionGuard>} />
      <Route path="/edit-order/:id" element={guarded("orders.edit", EditOrder)} />

      {/* Reports */}
      <Route path="/Reports" element={guarded("reports.view", Reports)} />
      <Route path="/CategoryProductReport" element={guarded("reports.view", CategoryProductReport)} />
      <Route path="/currency-totals" element={guarded("reports.view", CurrencyTotals)} />
      <Route
        path="/reports/payables-receivables"
        element={
          <PermissionGuard any={["reports.payables_receivables", "reports.view"]}>
            <PayablesReceivablesReport />
          </PermissionGuard>
        }
      />
      <Route path="/reports/buying" element={guarded("reports.view", BuyingReport)} />
      <Route path="/reports/selling" element={guarded("reports.view", SellingReport)} />

      {/* Units */}
      <Route path="/AddUnit" element={guarded("units.add", AddUnit)} />
      <Route path="/GetUnit" element={guarded("units.index", GetUnit)} />
      <Route path="/GetUnit/UpdateUnit/:id" element={guarded("units.edit", UpdateUnit)} />

      {/* Categories */}
      <Route path="/categories" element={guarded("categories.index", GetCategory)} />
      <Route path="/categories/add" element={guarded("categories.add", AddCategory)} />
      <Route path="/categories/update/:id" element={guarded("categories.edit", UpdateCategory)} />

      {/* Roles & users (same gate: users.index) */}
      <Route path="/roles" element={guarded("users.index", GetRoles)} />
      <Route path="/users" element={guarded("users.index", GetUsers)} />
      <Route path="/settings/system" element={guarded("users.index", SystemSettings)} />
      <Route path="/settings/exchange-rates" element={guarded("users.index", ExchangeRates)} />
      <Route
        path="/settings/maintenance"
        element={
          <PermissionGuard any={["maintenance.reset", "users.index"]}>
            <Maintenance />
          </PermissionGuard>
        }
      />

      {/* Customer sub-pages (permission inherits from customers) */}
      <Route path="/GetCustomer/OrderHistory/:id" element={guarded("customers.index", CustomerOrderHistory)} />
      <Route path="/customer/:customerId/order/:orderId" element={guarded("orders.view", CustomerOrderDetail)} />
      <Route path="/GetCustomer/PaymentRecipt/:id/:paymentId" element={guarded("customers.index", PaymentRecipt)} />
      <Route path="/GetCustomer/PaymentRecipt/:id" element={guarded("customers.index", PaymentRecipt)} />
      <Route path="/GetCustomer/CustomerPaymentHistory/:id" element={guarded("customers.index", CustomerPaymentHistory)} />
      <Route path="/GetCustomer/CombinedPayments/:id" element={guarded("customers.index", CombinedPayments)} />

      {/* 404 */}
      <Route path="/NotFound" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
